import React, { useEffect, useRef, useState } from "react";
import { http } from "../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../consts/api";
import { uiNotification } from "../../../services/UINotificationService";
import PharmacyOrderClient from "./PharmacyOrderClient";
import styles from "./ProcedureWellSyncPrescriptions.module.scss";

/**
 * Embeds WellSync pharmacy.js (https://github.com/wellsync/pharmacy.js) for CareConnect ordering.
 * Gated by REACT_APP_WELLSYNC_USE_PHARMACY_WIDGET=true and a PKCS8 PEM stored on the clinic config.
 */
function ProcedureWellSyncPharmacyWidget({
  clinicId,
  procedureId,
  clientId,
  procedureProviderId,
  prescriber,
  onBack,
}) {
  const mountRef = useRef(null);
  const widgetRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await http.post(HTTP_ENDPOINTS.wellsync.pharmacyWidgetConfig(), {
          clinicId,
        });
        const cfg = res.data && res.data.data;
        if (!cfg || !cfg.session || !cfg.privateKeyPem) {
          throw new Error("Invalid pharmacy widget configuration");
        }
        const clinicianId = String(
          procedureProviderId || prescriber?.externalId || "",
        );
        if (!clinicianId) {
          throw new Error("Provider is required for WellSync pharmacy");
        }
        if (cancelled || !mountRef.current) {
          return;
        }
        const mountId = `wellsync-pharmacy-mount-${procedureId}-${Date.now()}`;
        mountRef.current.id = mountId;

        const options = {
          case: `ar-ws-${procedureId}-${Date.now()}`,
          clinic: cfg.wellsyncClinicExternalId,
          clinician: clinicianId,
          patient: String(clientId),
          environment: cfg.environment,
          privateKey: cfg.privateKeyPem,
        };
        if (cfg.iframeOrigin) {
          options.widgetIframeOrigin = cfg.iframeOrigin;
        }
        widgetRef.current = new PharmacyOrderClient(
          `#${mountId}`,
          cfg.session,
          options,
        );
      } catch (e) {
        if (!cancelled) {
          const msg =
            e.response?.data?.message ||
            e.message ||
            "Could not load WellSync pharmacy widget";
          setError(msg);
          uiNotification.error(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (
        widgetRef.current &&
        typeof widgetRef.current.destroy === "function"
      ) {
        widgetRef.current.destroy();
        widgetRef.current = null;
      }
    };
  }, [clinicId, procedureId, clientId, procedureProviderId, prescriber && prescriber.externalId]);

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.btnSecondary} onClick={onBack}>
          Back
        </button>
      </div>
      {error ? <p className={styles.muted}>{error}</p> : null}
      <p className={styles.muted}>
        WellSync pharmacy.js (embedded). Requires HTTPS so the browser can sign
        prescription requests.
      </p>
      <div ref={mountRef} style={{ minHeight: 560 }} />
    </div>
  );
}

export default ProcedureWellSyncPharmacyWidget;
